import { adjustByGreekPrefix, greekPrefix } from 'core/util';
import { formatDateTime } from 'core/util/dateUtils';
import {
  DNS_RESPONSE_CODES,
  HEALTH_STATUS,
  HTTP_STATUS_CODES,
  type DnsCode,
  type HealthCode,
  type HttpStatusCode
} from '@kentik/ui-shared/query/unitEnums';

/**
 * Unit format types determine how values should be formatted
 */
type UnitFormatType =
  | 'greek'
  | 'duration'
  | 'timestamp'
  | 'percent'
  | 'integer'
  | 'health'
  | 'dns_code'
  | 'http_status_code';

interface UnitFormat {
  type: UnitFormatType;
  baseUnit?: string; // 'microseconds', 'milliseconds', 'seconds' for duration
  suffix?: string; // 'bits/s', '%', etc.
}

interface FormattedValue {
  value: number; // original value
  adjusted: number; // value after scaling
  display: string; // formatted number (e.g., "125.5")
  displayFull: string; // formatted with units (e.g., "125.5 ms")
  suffix: string; // the unit suffix (e.g., "ms", "Gbits/s")
}

/**
 * Enumeration-unit value maps live in shared so the measurement dictionary can
 * use them to populate metric `Values`. Re-exported here for existing consumers.
 */
export { HEALTH_STATUS, DNS_RESPONSE_CODES, HTTP_STATUS_CODES } from '@kentik/ui-shared/query/unitEnums';

/**
 * Duration units in ascending order of magnitude
 * Note: We use milliseconds as the smallest unit to avoid the µs symbol.
 * Sub-millisecond values will display as decimals (e.g., "0.175 ms" for 175µs)
 */
const DURATION_UNITS = [
  { name: 'milliseconds', label: 'ms', microseconds: 1000 },
  { name: 'seconds', label: 's', microseconds: 1_000_000 },
  { name: 'minutes', label: 'min', microseconds: 60_000_000 },
  { name: 'hours', label: 'hr', microseconds: 3_600_000_000 }
];

/**
 * Parse a unit string from measurements.ts into a structured format
 *
 * @example
 * parseUnit('duration:microseconds') → { type: 'duration', baseUnit: 'microseconds' }
 * parseUnit('percent') → { type: 'percent', suffix: '%' }
 * parseUnit('bits/s') → { type: 'greek', suffix: 'bits/s' }
 * parseUnit('integer') → { type: 'integer' }
 * parseUnit('health') → { type: 'health' }
 */
export function parseUnit(unit?: string): UnitFormat {
  if (!unit) {
    return { type: 'greek' };
  }

  if (unit.startsWith('duration')) {
    return { type: 'duration', baseUnit: unit.split(':')[1] || 'seconds' };
  }

  if (unit.startsWith('timestamp')) {
    return { type: 'timestamp', baseUnit: unit.split(':')[1] || 'nanoseconds' };
  }

  if (unit === 'percent') {
    return { type: 'percent', suffix: '%' };
  }

  if (unit === 'integer') {
    return { type: 'integer' };
  }

  if (unit === 'health') {
    return { type: 'health' };
  }

  if (unit === 'dns_code') {
    return { type: 'dns_code' };
  }

  if (unit === 'http_status_code') {
    return { type: 'http_status_code' };
  }

  // Default: use greek prefixes with the unit as suffix
  return { type: 'greek', suffix: unit };
}

/**
 * Format a duration value, auto-scaling to the most appropriate unit
 *
 * @param valueInMicroseconds - The value in microseconds
 * @param fix - Number of decimal places (default: 2)
 * @returns Formatted duration string
 */
function formatDuration(valueInMicroseconds: number, fix = 2): FormattedValue {
  if (valueInMicroseconds === 0) {
    return {
      value: 0,
      adjusted: 0,
      display: '0',
      displayFull: '0 ms',
      suffix: 'ms'
    };
  }

  const absValue = Math.abs(valueInMicroseconds);

  // Find the best unit (largest unit where value >= 1)
  // Default to milliseconds (smallest unit we display)
  let bestUnit = DURATION_UNITS[0]; // milliseconds
  for (let i = DURATION_UNITS.length - 1; i >= 0; i--) {
    if (absValue >= DURATION_UNITS[i].microseconds) {
      bestUnit = DURATION_UNITS[i];
      break;
    }
  }

  const adjusted = valueInMicroseconds / bestUnit.microseconds;
  const display = adjusted.toFixed(fix);

  return {
    value: valueInMicroseconds,
    adjusted,
    display,
    displayFull: `${display} ${bestUnit.label}`,
    suffix: bestUnit.label
  };
}

function formatTimestamp(value: number, baseUnit: string = 'nanoseconds'): FormattedValue {
  const factors = { seconds: 1000, milliseconds: 1, microseconds: 0.001, nanoseconds: 0.000001 };
  const factor = factors[baseUnit as keyof typeof factors] || 1;
  const display = formatDateTime(value * factor);
  return { value, adjusted: value, display, displayFull: display, suffix: '' };
}

/**
 * Convert a value from its base unit to microseconds
 */
function toMicroseconds(value: number, baseUnit: string): number {
  const unit = DURATION_UNITS.find((u) => u.name === baseUnit);
  if (!unit) {
    // If unknown unit, assume microseconds
    return value;
  }
  return value * unit.microseconds;
}

/**
 * Format a value with greek prefixes (K, M, G, T, P)
 */
function formatGreek(value: number, options: { suffix?: string; fix?: number; forcePrefix?: string }): FormattedValue {
  const { suffix = '', fix = 2, forcePrefix } = options;

  // Check if the suffix already starts with a greek prefix (e.g., 'Mbits/s')
  // In this case, don't add another prefix - just display as-is
  const greekPrefixes = ['K', 'M', 'G', 'T', 'P'];
  const suffixHasPrefix = suffix && greekPrefixes.some((p) => suffix.startsWith(p));

  if (suffixHasPrefix) {
    // Unit already has a prefix baked in, don't scale the value
    const display = value.toFixed(fix);
    return {
      value,
      adjusted: value,
      display,
      displayFull: suffix ? `${display} ${suffix}` : display,
      suffix
    };
  }

  const prefix = forcePrefix ?? greekPrefix([value]);
  const adjusted = adjustByGreekPrefix(value, prefix);
  const display = adjusted.toFixed(fix);
  const fullSuffix = prefix ? `${prefix}${suffix}` : suffix;

  return {
    value,
    adjusted,
    display,
    displayFull: fullSuffix ? `${display} ${fullSuffix}` : display,
    suffix: fullSuffix
  };
}

/**
 * Format a percentage value
 */
function formatPercent(value: number, fix = 2): FormattedValue {
  const display = value.toFixed(fix);
  return {
    value,
    adjusted: value,
    display,
    displayFull: `${display}%`,
    suffix: '%'
  };
}

/**
 * Format an integer value (no scaling)
 */
function formatInteger(value: number): FormattedValue {
  const display = Math.round(value).toString();
  return {
    value,
    adjusted: value,
    display,
    displayFull: display,
    suffix: ''
  };
}

/**
 * Format a health status value
 *
 * Health values are discrete codes (100, 200, 300) that map to status strings.
 * When values are averaged (which is semantically incorrect), we round to the nearest
 * valid health code and display the corresponding label.
 *
 * @param value - The health code value (100, 200, 300) or an averaged value
 * @returns Formatted health status
 */
export function formatHealth(value: number): FormattedValue {
  // Round to the nearest valid health code
  const validCodes: HealthCode[] = [100, 200, 300];
  const nearestCode = validCodes.reduce((prev, curr) =>
    Math.abs(curr - value) < Math.abs(prev - value) ? curr : prev
  );

  const status = HEALTH_STATUS[nearestCode];
  const label = status?.label || 'Unknown';

  return {
    value,
    adjusted: nearestCode,
    display: label,
    displayFull: label,
    suffix: ''
  };
}

/**
 * Get health status info including color for chart styling
 */
export function getHealthStatus(value: number): { code: HealthCode; label: string; color: string } {
  const validCodes: HealthCode[] = [100, 200, 300];
  const nearestCode = validCodes.reduce((prev, curr) =>
    Math.abs(curr - value) < Math.abs(prev - value) ? curr : prev
  );

  const status = HEALTH_STATUS[nearestCode];
  return {
    code: nearestCode,
    label: status?.label || 'Unknown',
    color: status?.color || '#9e9e9e'
  };
}

/**
 * Get all valid health codes for Y-axis tick positions
 */
export function getHealthTickPositions(): number[] {
  return [100, 200, 300];
}

/**
 * Get health label for Y-axis tick formatting
 */
export function getHealthLabel(value: number): string {
  const code = Math.round(value) as HealthCode;
  return HEALTH_STATUS[code]?.label || '';
}

/**
 * Format a DNS response code value
 *
 * DNS codes are discrete integer values (0-10) that map to standard RCODE names.
 * Display format: "0 No Error", "3 NXDomain", etc.
 *
 * @param value - The DNS response code (0-10)
 * @returns Formatted DNS code
 */
function formatDnsCode(value: number): FormattedValue {
  const code = Math.round(value) as DnsCode;
  const dnsInfo = DNS_RESPONSE_CODES[code];
  const label = dnsInfo ? `${code} ${dnsInfo.label}` : `${code} Unknown`;

  return {
    value,
    adjusted: code,
    display: label,
    displayFull: label,
    suffix: ''
  };
}

/**
 * Get DNS response code info
 */
export function getDnsCodeInfo(value: number): { code: number; label: string; description: string } {
  const code = Math.round(value) as DnsCode;
  const dnsInfo = DNS_RESPONSE_CODES[code];
  return {
    code,
    label: dnsInfo?.label || 'Unknown',
    description: dnsInfo?.description || 'Unknown DNS response code'
  };
}

/**
 * Get all valid DNS codes for Y-axis tick positions
 */
export function getDnsCodeTickPositions(): number[] {
  return Object.keys(DNS_RESPONSE_CODES).map(Number);
}

/**
 * Get DNS code label for Y-axis tick formatting
 */
export function getDnsCodeLabel(value: number): string {
  const code = Math.round(value) as DnsCode;
  const dnsInfo = DNS_RESPONSE_CODES[code];
  return dnsInfo ? `${code} ${dnsInfo.label}` : '';
}

/**
 * Format an HTTP status code value
 *
 * HTTP status codes are discrete integer values (100-599) that map to standard status text.
 * Display format: "200 OK", "404 Not Found", etc.
 *
 * @param value - The HTTP status code
 * @returns Formatted HTTP status code
 */
function formatHttpStatusCode(value: number): FormattedValue {
  const code = Math.round(value) as HttpStatusCode;
  const httpInfo = HTTP_STATUS_CODES[code];
  const label = httpInfo ? `${code} ${httpInfo.label}` : `${code}`;

  return {
    value,
    adjusted: code,
    display: label,
    displayFull: label,
    suffix: ''
  };
}

/**
 * Get HTTP status code info
 */
export function getHttpStatusCodeInfo(value: number): { code: number; label: string; category: string } {
  const code = Math.round(value) as HttpStatusCode;
  const httpInfo = HTTP_STATUS_CODES[code];
  return {
    code,
    label: httpInfo?.label || 'Unknown',
    category: httpInfo?.category || 'Unknown'
  };
}

/**
 * Get HTTP status code label for Y-axis tick formatting
 */
export function getHttpStatusCodeLabel(value: number): string {
  const code = Math.round(value) as HttpStatusCode;
  const httpInfo = HTTP_STATUS_CODES[code];
  return httpInfo ? `${code} ${httpInfo.label}` : `${code}`;
}

export interface FormatValueOptions {
  fix?: number;
  forcePrefix?: string; // For greek formatting, force a specific prefix
  values?: Record<string | number, string> | null; // Enum values map for label resolution
}

/**
 * Format a metric value based on its unit type
 *
 * @param value - The raw numeric value
 * @param unit - The unit string from measurements.ts (e.g., 'duration:microseconds', 'bits/s', 'percent')
 * @param options - Formatting options
 * @returns Formatted value object
 *
 * @example
 * formatMetricValue(175000, 'duration:microseconds') → { displayFull: '175 ms', ... }
 * formatMetricValue(1500000000, 'bits/s') → { displayFull: '1.5 Gbits/s', ... }
 * formatMetricValue(45.5, 'percent') → { displayFull: '45.50%', ... }
 */
export function formatMetricValue(
  value: number | null | undefined,
  unit?: string,
  options: FormatValueOptions = {}
): FormattedValue {
  // Handle null/undefined values
  if (value == null || Number.isNaN(value)) {
    return {
      value: 0,
      adjusted: 0,
      display: '-',
      displayFull: '-',
      suffix: ''
    };
  }

  const { fix = 2, forcePrefix, values } = options;

  // Enum resolution: if values map is provided and value matches, return label
  if (values) {
    const label = values[value] ?? values[String(value)];
    if (label != null) {
      const displayFull = unit === 'health' ? label : `${label} (${value})`;
      return {
        value,
        adjusted: value,
        display: label,
        displayFull,
        suffix: ''
      };
    }
  }

  const unitFormat = parseUnit(unit);

  switch (unitFormat.type) {
    case 'duration': {
      const valueInMicroseconds = toMicroseconds(value, unitFormat.baseUnit || 'microseconds');
      return formatDuration(valueInMicroseconds, fix);
    }
    case 'timestamp': {
      return formatTimestamp(value, unitFormat.baseUnit || 'nanoseconds');
    }
    case 'percent':
      return formatPercent(value, fix);
    case 'integer':
      return formatInteger(value);
    case 'health':
      return formatHealth(value);
    case 'dns_code':
      return formatDnsCode(value);
    case 'http_status_code':
      return formatHttpStatusCode(value);
    case 'greek':
    default:
      return formatGreek(value, { suffix: unitFormat.suffix, fix, forcePrefix });
  }
}

/**
 * Determine the best prefix for a dataset (for consistent axis labeling)
 *
 * For greek units, returns just the greek prefix (K, M, G, etc.) to be used with forcePrefix.
 * For duration, returns the full suffix (ms, s, min, etc.).
 * For other types, returns the suffix or empty string.
 *
 * @param data - Array of values
 * @param unit - The unit string from measurements.ts
 * @returns The prefix to use for the entire dataset
 */
export function getDatasetPrefix(data: number[], unit?: string): string {
  const unitFormat = parseUnit(unit);

  if (unitFormat.type === 'duration') {
    // For duration, find the best unit for the median value
    const sortedData = [...data].filter((v) => v != null && !Number.isNaN(v)).sort((a, b) => a - b);
    if (sortedData.length === 0) {
      return 'ms';
    }
    const medianValue = sortedData[Math.floor(sortedData.length / 2)];
    const valueInMicroseconds = toMicroseconds(medianValue, unitFormat.baseUnit || 'microseconds');
    return formatDuration(valueInMicroseconds).suffix;
  }

  if (unitFormat.type === 'timestamp') {
    // Timestamp doesn't use prefixes - values are formatted as full timestamps
    return '';
  }

  if (unitFormat.type === 'health') {
    // Health doesn't use prefixes - values are discrete codes
    return '';
  }

  if (unitFormat.type === 'dns_code') {
    // DNS codes don't use prefixes - values are discrete codes
    return '';
  }

  if (unitFormat.type === 'percent' || unitFormat.type === 'integer') {
    return unitFormat.suffix || '';
  }

  // Greek prefix - but skip if the unit already starts with a greek prefix (M, G, K, etc.)
  const defaultGreekPrefixes = ['K', 'M', 'G', 'T', 'P'];
  const unitHasPrefix = unitFormat.suffix && defaultGreekPrefixes.some((p) => unitFormat.suffix?.startsWith(p));

  if (unitHasPrefix) {
    // Unit already has a prefix baked in (e.g., 'Mbits/s'), return full suffix
    return unitFormat.suffix || '';
  }

  // Return just the greek prefix (K, M, G, etc.) - not the full suffix
  // This is used with forcePrefix in formatMetricValue
  return greekPrefix(data);
}

/**
 * Check if a unit type requires special Y-axis handling (like discrete tick positions)
 */
export function isDiscreteUnit(unit?: string): boolean {
  const unitFormat = parseUnit(unit);
  return unitFormat.type === 'health' || unitFormat.type === 'dns_code';
}
